import { useState, useCallback, useRef, useEffect } from 'react';
import { nanoid } from 'nanoid';
import { useGetBackendUrl } from '@/hooks/use-get-backend-url';
import { useIdentity } from '@/hooks/useIdentity';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { createAuthHeaders } from '@/utils/authHeaders';
import type {
  WorkflowAssistantMessage,
  WorkflowAssistantStreamEvent,
  WorkflowDefinitionDto,
  WorkflowPatch,
} from './types';

interface UseWorkflowAssistantOptions {
  workflowId: string;
  onWorkflowPatch?: (patch: WorkflowPatch) => void;
}

interface UseWorkflowAssistantReturn {
  messages: WorkflowAssistantMessage[];
  isLoading: boolean;
  isLoadingSession: boolean;
  error: string | null;
  sessionId: string | null;
  sendMessage: (
    message: string,
    workflowDefinition: WorkflowDefinitionDto,
    allowUpdates: boolean
  ) => Promise<void>;
  loadSession: () => Promise<void>;
  clearMessages: () => void;
  clearError: () => void;
}

export function useWorkflowAssistant({
  workflowId,
  onWorkflowPatch,
}: UseWorkflowAssistantOptions): UseWorkflowAssistantReturn {
  const backendUrl = useGetBackendUrl();
  const { getAccessToken } = useIdentity();
  const { subscriptionId } = useSubscription();

  const [messages, setMessages] = useState<WorkflowAssistantMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [hasLoadedSession, setHasLoadedSession] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  // Load existing session from backend
  const loadSession = useCallback(async () => {
    if (hasLoadedSession || !workflowId || !subscriptionId) {
      return;
    }

    setIsLoadingSession(true);

    try {
      const token = await getAccessToken();
      const url = `${backendUrl}/api/workflow-assistant/session/${workflowId}`;
      const headers = createAuthHeaders(token, subscriptionId);

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (response.ok) {
        const sessionData = await response.json();

        if (sessionData.sessionId) {
          setSessionId(sessionData.sessionId);
        }

        if (sessionData.messages && sessionData.messages.length > 0) {
          // Convert backend messages to frontend format
          const loadedMessages: WorkflowAssistantMessage[] = sessionData.messages.map(
            (msg: { id: string; role: string; content: string; timestamp: string }) => ({
              id: msg.id || nanoid(),
              role: msg.role as 'user' | 'assistant',
              content: msg.content,
              timestamp: new Date(msg.timestamp),
              status: 'sent' as const,
            })
          );
          setMessages(loadedMessages);
          console.log('[WorkflowAssistant] Loaded', loadedMessages.length, 'messages from session');
        }
      } else if (response.status !== 404) {
        // 404 is expected when no session exists
        console.warn('[WorkflowAssistant] Failed to load session:', response.status);
      }
    } catch (err) {
      console.error('[WorkflowAssistant] Error loading session:', err);
    } finally {
      setIsLoadingSession(false);
      setHasLoadedSession(true);
    }
  }, [workflowId, subscriptionId, backendUrl, getAccessToken, hasLoadedSession]);

  // Auto-load session when hook initializes
  useEffect(() => {
    if (workflowId && subscriptionId && !hasLoadedSession) {
      loadSession();
    }
  }, [workflowId, subscriptionId, hasLoadedSession, loadSession]);

  const sendMessage = useCallback(
    async (
      message: string,
      workflowDefinition: WorkflowDefinitionDto,
      allowUpdates: boolean
    ) => {
      if (isLoading) return;

      // Guard: Check if subscription context is available
      if (!subscriptionId) {
        setError('Subscription context not available. Please refresh the page.');
        console.error('[WorkflowAssistant] No subscriptionId available');
        return;
      }

      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const userMessage: WorkflowAssistantMessage = {
        id: nanoid(),
        role: 'user',
        content: message,
        timestamp: new Date(),
        status: 'sending',
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessToken();
        const url = `${backendUrl}/api/workflow-assistant/chat`;

        abortControllerRef.current = new AbortController();

        const headers = createAuthHeaders(token, subscriptionId);
        console.log('[WorkflowAssistant] Request headers:', {
          url,
          subscriptionId,
          hasToken: !!token,
          headers: Object.fromEntries(Object.entries(headers)),
        });

        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            workflowId,
            message,
            sessionId,
            workflowDefinition,
            allowUpdates,
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to send message');
        }

        // Update user message status
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === userMessage.id ? { ...msg, status: 'sent' as const } : msg
          )
        );

        // Read streaming response
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let aiResponseContent = '';
        const aiMessageId = nanoid();
        let buffer = '';

        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            buffer += chunk;

            // Split by double newline (SSE message separator)
            const sseMessages = buffer.split('\n\n');
            buffer = sseMessages.pop() || '';

            for (const sseMessage of sseMessages) {
              const lines = sseMessage.split('\n');
              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const data: WorkflowAssistantStreamEvent = JSON.parse(
                      line.slice(6)
                    );

                    if (data.type === 'chunk') {
                      aiResponseContent += data.content || '';

                      // Update or create AI message
                      setMessages((prev) => {
                        const existingIndex = prev.findIndex(
                          (m) => m.id === aiMessageId
                        );

                        const aiMessage: WorkflowAssistantMessage = {
                          id: aiMessageId,
                          role: 'assistant',
                          content: aiResponseContent,
                          timestamp: new Date(),
                          status: 'sent',
                        };

                        if (existingIndex >= 0) {
                          const newMessages = [...prev];
                          newMessages[existingIndex] = aiMessage;
                          return newMessages;
                        } else {
                          return [...prev, aiMessage];
                        }
                      });
                    } else if (data.type === 'tool_use') {
                      // Log tool call details
                      console.log('%c[WorkflowAssistant] 🔧 TOOL CALL', 'color: #3b82f6; font-weight: bold', {
                        tool: data.toolCall?.name,
                        id: data.toolCall?.id,
                        arguments: data.toolCall?.arguments,
                      });

                      // Update message with tool call info
                      setMessages((prev) => {
                        const existingIndex = prev.findIndex(
                          (m) => m.id === aiMessageId
                        );
                        if (existingIndex >= 0) {
                          const newMessages = [...prev];
                          const existingMessage = newMessages[existingIndex];
                          newMessages[existingIndex] = {
                            ...existingMessage,
                            toolCalls: [
                              ...(existingMessage.toolCalls || []),
                              data.toolCall!,
                            ],
                          };
                          return newMessages;
                        }
                        return prev;
                      });
                    } else if (data.type === 'tool_result') {
                      // Log tool result details
                      const resultColor = data.toolResult?.success ? '#22c55e' : '#ef4444';
                      console.log(`%c[WorkflowAssistant] ${data.toolResult?.success ? '✅' : '❌'} TOOL RESULT`, `color: ${resultColor}; font-weight: bold`, {
                        tool: data.toolResult?.toolName,
                        success: data.toolResult?.success,
                        result: data.toolResult?.result,
                        error: data.toolResult?.error,
                      });

                      // Log workflow patch if present
                      if (data.workflowPatch) {
                        console.log('%c[WorkflowAssistant] 📝 WORKFLOW PATCH', 'color: #a855f7; font-weight: bold', {
                          addNodes: data.workflowPatch.addNodes,
                          updateNodes: data.workflowPatch.updateNodes,
                          deleteNodes: data.workflowPatch.deleteNodes,
                          addEdges: data.workflowPatch.addEdges,
                          deleteEdges: data.workflowPatch.deleteEdges,
                        });
                      }

                      // Update message with tool result
                      setMessages((prev) => {
                        const existingIndex = prev.findIndex(
                          (m) => m.id === aiMessageId
                        );
                        if (existingIndex >= 0) {
                          const newMessages = [...prev];
                          const existingMessage = newMessages[existingIndex];
                          newMessages[existingIndex] = {
                            ...existingMessage,
                            toolResults: [
                              ...(existingMessage.toolResults || []),
                              data.toolResult!,
                            ],
                          };
                          return newMessages;
                        }
                        return prev;
                      });

                      // Apply workflow patch if provided
                      if (data.workflowPatch && onWorkflowPatch) {
                        onWorkflowPatch(data.workflowPatch);
                      }
                    } else if (data.type === 'done') {
                      console.log('%c[WorkflowAssistant] ✨ RESPONSE COMPLETE', 'color: #22c55e; font-weight: bold', {
                        sessionId: data.sessionId,
                        responseLength: data.fullResponse?.length,
                      });

                      // Update session ID
                      if (data.sessionId) {
                        setSessionId(data.sessionId);
                      }

                      // Final update to message
                      setMessages((prev) => {
                        const existingIndex = prev.findIndex(
                          (m) => m.id === aiMessageId
                        );
                        if (existingIndex >= 0) {
                          const newMessages = [...prev];
                          newMessages[existingIndex] = {
                            ...newMessages[existingIndex],
                            content:
                              data.fullResponse ||
                              newMessages[existingIndex].content,
                          };
                          return newMessages;
                        }
                        return prev;
                      });
                    } else if (data.type === 'error') {
                      console.error('%c[WorkflowAssistant] ❌ ERROR', 'color: #ef4444; font-weight: bold', {
                        error: data.error,
                      });

                      setError(data.error || 'Unknown error');

                      // Add error message
                      setMessages((prev) => [
                        ...prev,
                        {
                          id: nanoid(),
                          role: 'assistant',
                          content: `Error: ${data.error}`,
                          timestamp: new Date(),
                          status: 'error',
                        },
                      ]);
                    }
                  } catch (parseError) {
                    console.error('Error parsing SSE data:', parseError);
                  }
                }
              }
            }
          }
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          // Request was cancelled
          return;
        }

        const errorMessage =
          err instanceof Error ? err.message : 'Failed to send message';
        setError(errorMessage);

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === userMessage.id ? { ...msg, status: 'error' as const } : msg
          )
        );
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    },
    [backendUrl, getAccessToken, subscriptionId, workflowId, sessionId, isLoading, onWorkflowPatch]
  );

  const clearMessages = useCallback(async () => {
    // Delete session from backend first (permanent deletion)
    if (workflowId && subscriptionId) {
      try {
        const token = await getAccessToken();
        const url = `${backendUrl}/api/workflow-assistant/session/${workflowId}`;
        const headers = createAuthHeaders(token, subscriptionId);

        const response = await fetch(url, {
          method: 'DELETE',
          headers,
        });

        if (!response.ok) {
          console.error('[WorkflowAssistant] Failed to delete session:', response.status);
        } else {
          console.log('[WorkflowAssistant] Session deleted from backend');
        }
      } catch (err) {
        console.error('[WorkflowAssistant] Error deleting session:', err);
      }
    }

    // Clear local state
    setMessages([]);
    setSessionId(null);
    // Don't reset hasLoadedSession - this prevents immediate reload from backend
    // The session stays "loaded" (as empty) until they close/reopen the panel
  }, [workflowId, subscriptionId, backendUrl, getAccessToken]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    isLoadingSession,
    error,
    sessionId,
    sendMessage,
    loadSession,
    clearMessages,
    clearError,
  };
}
